/**
 * games.ts — mini-games "today" stats + finish-and-collect-reward
 * (PR-15 split of api/client.ts).
 *
 * Daily / hourly capped server-side — the client just submits score and
 * the server clamps the reward against remaining cap. RewardBlocked also
 * lives here because shop, quiz, and wishes all reuse the same shape to
 * tell the UI "you would have earned X, but parent controls or daily
 * caps blocked it"; co-locating it with games avoids a free-floating
 * types-only module.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by GamesPage (lobby), MathBlitzPage, MemoryDigitsPage,
 *     PotionPuzzlePage, AgeMiniGamePage.
 *   - Backend: backend/src/routes/games.js + caps logic in lib/play_caps.js.
 *
 * 关键约束：
 *   - Both endpoints forward deviceTz() so "today" + "this hour" align
 *     with the kid's local clock.
 *   - next_eligible_at is epoch ms (0 when not capped) — UI shows a
 *     countdown using `Date.now()` against this value.
 */
import { call, deviceTz } from './_core';
import type { BondGainResponse } from './pets';

export interface GameTodayStat {
  code: string;                  // 'math_blitz' | 'memory_digits' | ...
  title_zh?: string;
  title_en?: string;
  age_min?: number;
  age_max?: number;
  age_recommended?: boolean;
  parent_enabled?: boolean;
  category?: string;
  played_today: number;
  total_score_today: number;
  reward_today_sen: number;
  daily_reward_cap_sen: number;
  daily_reward_capped?: boolean;
  // v0.10.2 — per-hour cap (3 sessions / rolling 60 min)
  daily_play_cap: number;
  daily_play_capped?: boolean;
  plays_this_hour: number;
  hourly_play_cap: number;
  hourly_capped: boolean;
  next_eligible_at: number;       // epoch ms; 0 if not currently capped
  can_play: boolean;
  block_reason: 'daily_cap' | 'daily_play_cap' | 'hourly_cap' | 'parent_disabled' | null;
}

export interface GamesTodayRes {
  today: string;
  tz: string;
  child_age?: number | null;
  games: GameTodayStat[];
}

export interface GameFinishRes {
  ok: true;
  reward_sen: number;
  reward_blocked?: RewardBlocked | null;
  balance: number;
  total_today_sen: number;
  daily_reward_cap_sen: number;
  capped: boolean;
  daily_reward_capped?: boolean;
  session_id: number | null;
  played_today: number;
  daily_play_cap: number;
  daily_play_capped?: boolean;
  can_play_next?: boolean;
  bond_gain?: BondGainResponse | null;
  // v0.10.2 — hourly status post-insert so client can update UI immediately
  plays_this_hour: number;
  hourly_play_cap: number;
  next_eligible_at: number;       // epoch ms; 0 if not capped
}

export interface RewardBlocked {
  code: string;
  message: string;
  detail?: unknown;
}

export const gamesApi = {
  // ─── Mini-games (earn-coins side track) ───────────────────────────────
  // Daily-capped server-side; the client just submits score and the server
  // clamps the reward against remaining cap.
  gamesToday() {
    return call<GamesTodayRes>(
      `/api/v1/child/games/today?tz=${encodeURIComponent(deviceTz())}`,
    );
  },
  gameFinish(payload: { game_code: string; score: number; duration_ms: number }) {
    return call<GameFinishRes>('/api/v1/child/games/finish', {
      method: 'POST',
      body: JSON.stringify({ ...payload, tz: deviceTz() }),
    });
  },
};
